import React from "react";
import { motion } from "framer-motion";

/* ---------------- Reveal wrappers ---------------- */
export const Reveal = ({ children, delay = 0, y = 28, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Stagger = ({ children, className = "", gap = 0.08 }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    variants={{ hidden: {}, show: { transition: { staggerChildren: gap } } }}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "", y = 24 }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y },
      show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
    }}
  >
    {children}
  </motion.div>
);

/* ---------------- Section badge (blueprint slide number) ---------------- */
export const SectionBadge = ({ number, label, tone = "electric" }) => {
  const color = tone === "teal" ? "text-teal" : tone === "light" ? "text-electric-light" : "text-electric";
  const line = tone === "teal" ? "bg-teal/40" : "bg-electric/40";
  return (
    <div className="flex items-center gap-4" data-testid={`section-badge-${number}`}>
      <span className={`font-display font-black text-sm ${color}`}>{number}</span>
      <span className={`h-px w-10 ${line}`} />
      <span className="text-xs font-bold uppercase tracking-[0.25em] text-current/70">{label}</span>
    </div>
  );
};

/* ---------------- Feature icon card ---------------- */
export const FeatureCard = ({ icon: Icon, title, desc, tone = "electric", dark = false, testId }) => {
  const iconBg = tone === "teal" ? "bg-teal/12 text-teal" : "bg-electric/12 text-electric";
  return (
    <div
      data-testid={testId}
      className={`group relative rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1.5 ${
        dark
          ? "glass-dark hover:border-electric/40"
          : "bg-white border border-navy/[0.06] shadow-[0_8px_32px_rgba(30,127,240,0.08)] hover:shadow-[0_16px_48px_rgba(30,127,240,0.16)]"
      }`}
    >
      <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl ${iconBg} transition-transform duration-300 group-hover:scale-110`}>
        <Icon size={26} weight="duotone" />
      </div>
      <h3 className={`font-display text-lg font-bold mb-2 ${dark ? "text-white" : "text-navy"}`}>{title}</h3>
      <p className={`text-sm leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{desc}</p>
    </div>
  );
};

/* ---------------- CTA Button ---------------- */
export const PrimaryButton = ({ children, onClick, href, className = "", testId, tone = "electric" }) => {
  const bg =
    tone === "teal"
      ? "bg-teal hover:bg-teal/90 shadow-[0_8px_24px_rgba(16,185,129,0.35)]"
      : "bg-electric hover:bg-electric/90 shadow-[0_8px_24px_rgba(30,127,240,0.4)]";
  const cls = `group inline-flex items-center gap-2 rounded-full ${bg} px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 ${className}`;
  const inner = (
    <>
      {children}
      <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
    </>
  );
  if (href) return <a href={href} data-testid={testId} className={cls}>{inner}</a>;
  return <button onClick={onClick} data-testid={testId} className={cls}>{inner}</button>;
};

export const GhostButton = ({ children, onClick, href, className = "", testId, dark = false }) => {
  const cls = `inline-flex items-center gap-2 rounded-full border px-7 py-3.5 text-sm font-bold transition-all duration-300 hover:-translate-y-0.5 ${
    dark
      ? "border-white/25 text-white hover:bg-white/10"
      : "border-navy/20 text-navy hover:bg-navy hover:text-white"
  } ${className}`;
  if (href) return <a href={href} data-testid={testId} className={cls}>{children}</a>;
  return <button onClick={onClick} data-testid={testId} className={cls}>{children}</button>;
};

/* ---------------- Trust strip ---------------- */
export const TrustStrip = ({ items, tone = "electric", dark = false }) => {
  const dot = tone === "teal" ? "bg-teal" : "bg-electric";
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
      {items.map((t, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
          <span className={`text-sm font-semibold ${dark ? "text-slate-200" : "text-navy/80"}`}>{t}</span>
        </div>
      ))}
    </div>
  );
};

/* ---------------- Quote band ---------------- */
export const QuoteBand = ({ quote, tone = "electric" }) => (
  <div className="relative overflow-hidden rounded-[2rem] bg-navy-deep grain px-8 py-14 sm:px-16 sm:py-20">
    <div aria-hidden className="absolute inset-0 bg-dots opacity-40" />
    <div aria-hidden className={`absolute -right-16 -top-16 h-64 w-64 rounded-full blur-3xl ${tone === "teal" ? "bg-teal/20" : "bg-electric/20"}`} />
    <Reveal className="relative">
      <span className={`font-display text-6xl leading-none ${tone === "teal" ? "text-teal" : "text-electric"}`}>“</span>
      <p className="mt-2 max-w-3xl font-display text-2xl font-semibold leading-snug text-white sm:text-3xl">{quote}</p>
    </Reveal>
  </div>
);

/* ---------------- Dotted network globe (SVG) ---------------- */
export const DottedGlobe = ({ className = "", tone = "electric" }) => {
  const stroke = tone === "teal" ? "#10B981" : "#1E7FF0";
  return (
    <div className={`relative ${className}`} aria-hidden>
      <div className="absolute inset-0 rounded-full blur-3xl opacity-30" style={{ background: stroke }} />
      <svg viewBox="0 0 400 400" className="relative animate-spin-slow" style={{ filter: `drop-shadow(0 0 20px ${stroke}55)` }}>
        <circle cx="200" cy="200" r="160" fill="none" stroke={stroke} strokeOpacity="0.25" strokeWidth="1" />
        {[...Array(7)].map((_, i) => (
          <ellipse key={`h${i}`} cx="200" cy="200" rx="160" ry={22 + i * 22} fill="none" stroke={stroke} strokeOpacity="0.18" strokeWidth="1" />
        ))}
        {[...Array(7)].map((_, i) => (
          <ellipse key={`v${i}`} cx="200" cy="200" rx={22 + i * 22} ry="160" fill="none" stroke={stroke} strokeOpacity="0.18" strokeWidth="1" />
        ))}
        {[...Array(50)].map((_, i) => {
          const a = (i / 50) * Math.PI * 2;
          const r = 60 + (i % 5) * 24;
          return <circle key={`d${i}`} cx={200 + Math.cos(a) * r} cy={200 + Math.sin(a * 1.3) * r} r={i % 6 === 0 ? 3 : 1.6} fill={stroke} fillOpacity={0.7} />;
        })}
      </svg>
    </div>
  );
};

/* ---------------- Diagonal ribbon corner ---------------- */
export const RibbonCorner = ({ tone = "electric" }) => (
  <div aria-hidden className="absolute right-0 top-0 h-40 w-40 overflow-hidden">
    <div className={`absolute -right-20 -top-20 h-40 w-40 rotate-45 ${tone === "teal" ? "bg-teal/30" : "bg-electric/40"}`} />
  </div>
);
